import { create } from 'zustand';
import { Order, MainWorkOrder, SubWorkOrder, RushOrder } from '@/types';

interface OrderState {
  orders: Order[];
  mainWorkOrders: MainWorkOrder[];
  subWorkOrders: SubWorkOrder[];
  rushOrders: RushOrder[];

  // Actions
  setOrders: (orders: Order[]) => void;
  setMainWorkOrders: (orders: MainWorkOrder[]) => void;
  setSubWorkOrders: (orders: SubWorkOrder[]) => void;
  setRushOrders: (orders: RushOrder[]) => void;

  addOrder: (order: Order) => void;
  updateOrder: (id: string, updates: Partial<Order>) => void;
  deleteOrder: (id: string) => void;

  addMainWorkOrder: (order: MainWorkOrder) => void;
  updateMainWorkOrder: (id: string, updates: Partial<MainWorkOrder>) => void;

  addSubWorkOrder: (order: SubWorkOrder) => void;
  updateSubWorkOrder: (id: string, updates: Partial<SubWorkOrder>) => void;

  addRushOrder: (order: RushOrder) => void;
  updateRushOrder: (id: string, updates: Partial<RushOrder>) => void;

  // Getters
  getOrderById: (id: string) => Order | undefined;
  getMainWorkOrdersByOrderId: (orderId: string) => MainWorkOrder[];
  getSubWorkOrdersByMainOrderId: (mainOrderId: string) => SubWorkOrder[];
  getRushOrderById: (id: string) => RushOrder | undefined;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  // Initial state
  orders: [],
  mainWorkOrders: [],
  subWorkOrders: [],
  rushOrders: [],

  // Setters
  setOrders: (orders) => set({ orders }),
  setMainWorkOrders: (orders) => set({ mainWorkOrders: orders }),
  setSubWorkOrders: (orders) => set({ subWorkOrders: orders }),
  setRushOrders: (orders) => set({ rushOrders: orders }),

  // Order actions
  addOrder: (order) => set((state) => ({ orders: [...state.orders, order] })),

  updateOrder: (id, updates) =>
    set((state) => ({
      orders: state.orders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  deleteOrder: (id) =>
    set((state) => ({
      orders: state.orders.filter((order) => order.id !== id),
    })),

  // MainWorkOrder actions
  addMainWorkOrder: (order) =>
    set((state) => ({ mainWorkOrders: [...state.mainWorkOrders, order] })),

  updateMainWorkOrder: (id, updates) =>
    set((state) => ({
      mainWorkOrders: state.mainWorkOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // SubWorkOrder actions
  addSubWorkOrder: (order) =>
    set((state) => ({ subWorkOrders: [...state.subWorkOrders, order] })),

  updateSubWorkOrder: (id, updates) =>
    set((state) => ({
      subWorkOrders: state.subWorkOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // RushOrder actions
  addRushOrder: (order) =>
    set((state) => ({ rushOrders: [...state.rushOrders, order] })),

  updateRushOrder: (id, updates) =>
    set((state) => ({
      rushOrders: state.rushOrders.map((order) =>
        order.id === id ? { ...order, ...updates } : order
      ),
    })),

  // Getters
  getOrderById: (id) => get().orders.find((order) => order.id === id),

  getMainWorkOrdersByOrderId: (orderId) =>
    get().mainWorkOrders.filter((order) => order.orderId === orderId),

  getSubWorkOrdersByMainOrderId: (mainOrderId) =>
    get().subWorkOrders.filter((order) => order.mainOrderId === mainOrderId),

  getRushOrderById: (id) => get().rushOrders.find((order) => order.id === id),
}));
